import {
  Box,
  Typography,
  Table,
  TableHead,
  TableCell,
  TableRow,
  Hidden,
  TableBody,
  useTheme,
  makeStyles,
  Theme,
  useMediaQuery
} from "@material-ui/core";
import { colors } from "swyft-react-core";

import { Payment } from "../../../../api/Types";
import { QuoteDeduction } from "../../../../api/Quote/QuoteDeduction";
import { QuoteLineItem } from "../../../../api/Quote/QuoteLineItem";
import { Quote } from "../../../../api/Quote/Quote";
import {
  deductionSort,
  quoteDeductionValue,
  hasAddons,
  lineAddonSort,
  lineItemSort,
  // quoteBalanceOutstanding,
  quoteVat,
  quotePaymentsMade
} from "../../../../app/utils";
import { NewlineDisplay } from "../../../../components/NewlineDisplay";
import { FeatureAddons } from "../../../../app/consts";
import { useConfig } from "../../../../components/config/ConfigContext";
import { fontFamily } from "../../../../app/theme";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { PriceFormat } from "../../../../components/typography/PriceFormat";

const QuoteDetailsTable = ({ quote }: Props) => {
  const { isFeatureEnabled } = useConfig();

  const classes = useStyles();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const { isMoreThanSm, isMoreThanMd } = useBreakpoint();

  var minWidth = isSmall ? 80 : 150;

  if (quote === null) {
    return null;
  }
  const amountPayable = quote.price;
  const isSolar = quote.type.includes("Solar");
  const vat = isSolar ? 0 : quoteVat(quote);

  const itemsView = (items?: QuoteLineItem[]) => {
    if (!items) {
      return null;
    }
    return items
      .slice()
      .sort(lineItemSort)
      .map((li) => <CollapsibleRow key={li.productId} lineItem={li} />);
  };

  const addonsView = (addons?: QuoteLineItem[]) => {
    if (!isFeatureEnabled(FeatureAddons)) {
      return null;
    }

    if (!hasAddons(addons)) {
      return null;
    }

    if (
      addons!.filter((value) => {
        if (value.selected) {
          return true;
        }
        return false;
      }).length === 0
    ) {
      return null;
    }

    const addonRender = addons!
      .slice()
      .sort(lineAddonSort)
      .map((li) => {
        if (!li.selected) {
          return null;
        }
        return <CollapsibleRow key={li.productId} lineItem={li} />;
      });

    return (
      <>
        <TableRow>
          <TableCell className={classes.detailsCell} colSpan={5}>
            <Box fontWeight="fontWeightBold" padding={1} pl={isSmall ? 4 : 6}>
              Additional items included
            </Box>
          </TableCell>
        </TableRow>
        {addonRender}
      </>
    );
  };

  const deductionsView = (deductions?: QuoteDeduction[]) => {
    if (!deductions) {
      return null;
    }
    return deductions
      .slice()
      .sort(deductionSort)
      .map((d) => <DeductionRow key={d.id} deduction={d} />);
  };

  // const outstandingBalance = quoteBalanceOutstanding(quote);
  const paymentsMade: Payment[] = quotePaymentsMade(quote);

  return (
    <>
      <Table padding="none" className={classes.detailsTable}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Box
                borderTop={isSmall ? "1px solid #efefef" : "none"}
                borderBottom={isSmall ? "1px solid #efefef" : "none"}
                pl={isSmall ? "12px" : 0}
                py={isSmall ? "4px" : 0}
              >
                Item
              </Box>
            </TableCell>
            <TableCell align="center" width={minWidth}>
              <Box
                borderTop={isSmall ? "1px solid #efefef" : "none"}
                borderBottom={isSmall ? "1px solid #efefef" : "none"}
                pl={isSmall ? "0.5rem" : 0}
                py={isSmall ? "4px" : 0}
              >
                Qty
              </Box>
            </TableCell>
            <Hidden smDown>
              <TableCell align="right" width={minWidth}>
                <Box
                  borderTop={isSmall ? "1px solid #efefef" : "none"}
                  borderBottom={isSmall ? "1px solid #efefef" : "none"}
                  pl={isSmall ? "0.5rem" : 0}
                  py={isSmall ? "4px" : 0}
                >
                  <Typography noWrap={true} variant="inherit">
                    Per Unit
                  </Typography>
                </Box>
              </TableCell>
            </Hidden>
            <TableCell align="right" width={minWidth}>
              <Box
                borderTop={isSmall ? "1px solid #efefef" : "none"}
                borderBottom={isSmall ? "1px solid #efefef" : "none"}
                pl={isSmall ? "0.5rem" : 0}
                py={isSmall ? "4px" : 0}
              >
                {isSmall ? "COST" : "Amount (€)"}
              </Box>
            </TableCell>
            <TableCell width={isSmall ? "21px" : "auto"}>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {itemsView(quote.items)}
          {addonsView(quote.addons)}
          {deductionsView(quote.deductions)}
          {!isMoreThanMd && (
            <TableRow className={classes.resetRow}>
              <TableCell colSpan={4}>
                <Box height={"1px"} width={"100%"} bgcolor={"#efefef"} my="14px" />
              </TableCell>
            </TableRow>
          )}
          <TableRow className={classes.resetRow}>
            {!isSmall && (
              <TableCell className={classes.detailsCell} align="right">
                &nbsp;
              </TableCell>
            )}

            <TableCell
              colSpan={isSmall && isMoreThanMd ? 4 : 3}
              className={`${classes.detailsCell} ${isSmall ? "p-0" : ""}`}
              align={"left"}
            >
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                fontWeight={500}
                fontSize={isMoreThanSm ? "14px" : "12px"}
              >
                <Box paddingLeft={isSmall ? "17px" : `${minWidth / 2}px`}>VAT</Box>
                <Box>
                  <PriceFormat value={vat} />
                </Box>
              </Box>
            </TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>

          <TableRow className={classes.resetRow}>
            {!isSmall && (
              <TableCell className={classes.detailsCell} align="right">
                &nbsp;
              </TableCell>
            )}
            <TableCell
              colSpan={isSmall && isMoreThanMd ? 4 : 3}
              className={`${classes.detailsCell} ${isSmall ? "p-0" : ""}`}
              align={"left"}
            >
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                fontWeight={500}
                fontSize={!isSmall ? "16px" : isMoreThanSm ? "14px" : "12px"}
                color={!isSmall ? "#43335b" : "#4b5462"}
              >
                <Box paddingLeft={isSmall ? "17px" : `${minWidth / 2}px`}>Total (inc VAT)</Box>
                <Box
                  fontWeight={500}
                  fontSize={!isSmall ? "16px" : isMoreThanSm ? "14px" : "12px"}
                  color={!isSmall ? "#43335b" : "#4b5462"}
                >
                  <PriceFormat value={amountPayable} />
                </Box>
              </Box>
            </TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
          {paymentsMade.map((p, i) => (
            <TableRow key={i} className={classes.resetRow}>
              {!isSmall && (
                <TableCell className={classes.detailsCell} align="right">
                  &nbsp;
                </TableCell>
              )}
              <TableCell
                colSpan={isSmall && isMoreThanMd ? 4 : 3}
                className={`${classes.detailsCell} ${isSmall ? "p-0" : ""}`}
                align={"left"}
              >
                <Box
                  display={"flex"}
                  justifyContent={"space-between"}
                  fontWeight={500}
                  fontSize={isMoreThanSm ? "14px" : "12px"}
                >
                  <Box paddingLeft={isSmall ? "17px" : `${minWidth / 2}px`}>
                    {p.amount === quote.depositRequirement ? "Deposit Paid" : "Payment"}
                  </Box>
                  <Box color={!isSmall ? "#4b5462" : "#f7815c"}>
                    <PriceFormat value={p.amount * -1} />
                  </Box>
                </Box>
              </TableCell>
              <TableCell>&nbsp;</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default QuoteDetailsTable;

interface CollapsibleRowProps {
  lineItem: QuoteLineItem;
}

const CollapsibleRow = ({ lineItem }: CollapsibleRowProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const { isMoreThanSm } = useBreakpoint();

  let btnDelete = null;
  let quantity = <Box fontWeight={500}>{lineItem.quantity}</Box>;
  let name = lineItem.description;

  return (
    <>
      <TableRow>
        <TableCell className={`${classes.detailsCell} ${isSmall ? "pr-0" : ""}`}>
          <Box display="flex" alignItems="center">
            <NewlineDisplay text={name} />
          </Box>
        </TableCell>
        <TableCell className={`${classes.detailsCell} ${isSmall ? "pr-0" : ""}`} align="center">
          <Box fontSize={isMoreThanSm ? "14px" : "12px"}>{quantity}</Box>
        </TableCell>
        <Hidden smDown>
          <TableCell className={`${classes.detailsCell} ${isSmall ? "pr-0" : ""}`} align="right">
            <Box fontWeight={500}>
              <PriceFormat value={lineItem.price} />
            </Box>
          </TableCell>
        </Hidden>
        <TableCell className={`${classes.detailsCell} ${isSmall ? "pr-0" : ""}`} align="right">
          <Box fontWeight={500} fontSize={isMoreThanSm ? "14px" : "12px"}>
            <PriceFormat value={lineItem.quantity * lineItem.price} />
          </Box>
        </TableCell>
        <TableCell className={`${classes.detailsCell} ${isSmall ? "pr-0" : ""}`} align="right">
          {btnDelete}
        </TableCell>
      </TableRow>
    </>
  );
};

const DeductionRow = (props: { deduction: QuoteDeduction }) => {
  const classes = useStyles();
  const { isMoreThanMd, isMoreThanSm } = useBreakpoint();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const { deduction } = props;

  return (
    <>
      <TableRow>
        <TableCell
          colSpan={isMoreThanMd ? 4 : 3}
          className={`${classes.detailsCell} ${isSmall ? "pr-0" : ""}`}
          style={{ whiteSpace: "pre-wrap" }}
        >
          <Box display={"flex"} justifyContent={"space-between"}>
            {deduction.description}
            <Box
              fontWeight={500}
              fontSize={isMoreThanSm ? "14px" : "12px"}
              color={isMoreThanMd ? "#4b5462" : "#f7815c"}
            >
              <PriceFormat value={quoteDeductionValue(deduction)} />
            </Box>
          </Box>
        </TableCell>
        <TableCell className={classes.detailsCell} align="right">
          &nbsp;
        </TableCell>
      </TableRow>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  detailsTable: {
    "fontFamily": fontFamily.Axiforma,
    "& th:not(:first-child)": {
      paddingLeft: "0.5rem",
      [theme.breakpoints.down("md")]: {
        paddingLeft: "0rem",
        paddingRight: 0
      }
    },
    "& th": {
      fontWeight: "bold",
      border: 0,
      padding: "0.5rem",
      fontSize: "12px",
      textTransform: "uppercase",
      paddingLeft: theme.spacing(6),
      color: "#4b5462",
      fontFamily: fontFamily.Axiforma,
      backgroundColor: colors.white,
      [theme.breakpoints.down("md")]: {
        paddingLeft: "21px",
        paddingRight: 0,
        paddingBottom: "15px"
      }
    },
    "& td": {
      padding: "0.5rem",
      fontSize: "14px",
      lineHeight: "1.79",
      fontFamily: fontFamily.Axiforma,
      paddingLeft: theme.spacing(6),
      color: "#4b5462",
      borderBottom: "none",
      [theme.breakpoints.down("md")]: {
        paddingLeft: "33px"
      },
      [theme.breakpoints.down("xs")]: {
        fontSize: "10px"
      }
    },
    "& td:not(:first-child)": {
      paddingLeft: "0.5rem"
    },
    "& tr:nth-of-type(odd)": {
      backgroundColor: colors.alternate
    }
  },
  detailsCell: {
    "fontFamily": fontFamily.Axiforma,
    "borderBottom": "0 !important",
    "padding": "0.5rem 0",
    "verticalAlign": "middle",
    "&:last-child": {
      padding: "0.5rem 0"
    },
    "&.pr-0": {
      paddingRight: 0
    }
  },
  resetRow: {
    "backgroundColor": "#FFFFFF !important",
    "padding": "0.5rem 0",
    "& .p-0": {
      padding: 0,
      paddingLeft: theme.spacing(2),
      paddingTop: "5px"
    }
  },
  button: {
    width: "90%",
    maxWidth: "17.5rem"
  },
  btnDelete: {
    minWidth: "0px"
  },
  doubleButton: {
    '& [class*="Button-root"]': {
      minWidth: "10rem",
      padding: "6px 8px",
      width: "auto"
    }
  }
}));

interface Props {
  quote: Quote | null;
}
